import axios from 'axios';

import { paths } from 'src/routes/paths';

import {
  localStorageGetItem,
  localStorageSetItem,
  localStorageRemoveItem,
} from 'src/utils/local-storage';

import ScrollHint from 'src/components/kanban/scroll-hint';

// ----------------------------------------------------------------------

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );

  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string) => {
  if (!accessToken) {
    return false;
  }

  // a malformed token is unusable, not a crash — jwtDecode throws on anything that is not a JWT
  try {
    const decoded = jwtDecode(accessToken);

    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  } catch (error) {
    return false;
  }
};

// ----------------------------------------------------------------------

export const tokenExpired = (exp: number) => {
  // eslint-disable-next-line prefer-const
  let expiredTimer;

  const currentTime = Date.now();

  // Test token expires after 10s
  // const timeLeft = currentTime + 10000 - currentTime; // ~10s
  const timeLeft = exp * 1000 - currentTime;

  clearTimeout(expiredTimer);

  expiredTimer = setTimeout(() => {
    alert('Token expired');

    sessionStorage.removeItem('accessToken');

    window.location.href = paths.auth.login;
  }, timeLeft);
};

// ----------------------------------------------------------------------

export const setSession = (accessToken: string | null) => {
  if (accessToken) {
    localStorageSetItem('access_token', accessToken);

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorageRemoveItem('access_token');

    delete axios.defaults.headers.common.Authorization;
  }
};

export const getSessionToken = () => localStorageGetItem('access_token');

// JT1-1643: a 401 only proves the session is over when the token we hold is missing or expired.
// Production answered /auth/me with 401 116 times on 2026-09-01 for sessions that worked before
// and after the rejection, so treating every 401 as "session over" signed people out mid-work.
export const hasUsableSession = () => isValidToken(getSessionToken() || '');

export const logOut = () => {
  localStorageRemoveItem('access_token');
  localStorageRemoveItem('show_incoming_requests_modal');
  ScrollHint.resetShowCount();

  delete axios.defaults.headers.common.Authorization;
};
